import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '../config/api.config';

export interface CheckoutLinePayload {
  type: 'product' | 'bundle';
  id: string;
  quantity: number;
}

export interface CreateCheckoutSessionRequest {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  storefrontUrl: string;
  lines: CheckoutLinePayload[];
}

export function resolveStorefrontUrl(): string {
  return new URL('.', document.baseURI).href.replace(/\/$/, '');
}

export interface CheckoutConfig {
  publishableKey: string;
  taxRate: number;
}

export interface CheckoutPricedLine {
  id: string;
  name: string;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
}

export interface CreateCheckoutSessionResponse {
  sessionId: string;
  clientSecret: string;
  publishableKey: string;
  lines: CheckoutPricedLine[];
  subtotal: number;
  taxAmount: number;
  total: number;
}

const EMBEDDED_SESSION_KEY = 'ecommerce.checkout.embedded.scent.v1';

export interface EmbeddedCheckoutState {
  sessionId: string;
  clientSecret: string;
  publishableKey: string;
}

@Injectable({ providedIn: 'root' })
export class CheckoutService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${API_BASE_URL}/checkout`;

  getConfig(): Observable<CheckoutConfig> {
    return this.http.get<CheckoutConfig>(`${this.baseUrl}/config`);
  }

  createSession(
    request: CreateCheckoutSessionRequest,
    idempotencyKey: string
  ): Observable<CreateCheckoutSessionResponse> {
    const headers = new HttpHeaders({
      'Idempotency-Key': idempotencyKey
    });
    return this.http.post<CreateCheckoutSessionResponse>(`${this.baseUrl}/sessions`, request, { headers });
  }

  cancelSession(sessionId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/cancel`, { sessionId });
  }

  saveEmbeddedSession(state: EmbeddedCheckoutState): void {
    sessionStorage.setItem(EMBEDDED_SESSION_KEY, JSON.stringify(state));
  }

  readEmbeddedSession(): EmbeddedCheckoutState | null {
    const raw = sessionStorage.getItem(EMBEDDED_SESSION_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as EmbeddedCheckoutState;
    } catch {
      return null;
    }
  }

  clearEmbeddedSession(): void {
    sessionStorage.removeItem(EMBEDDED_SESSION_KEY);
  }
}
